import { useState, useRef, useEffect, useMemo } from "react";
import { useAppStore } from "@/lib/store";
import { useWorklog } from "../../lib/api/hooks";
import { useAgentStream } from "./useAgentStream";
import { Button } from "../../components/ui/button";
import { Loader2, User, Bot, Copy, Check } from "lucide-react";
import { Card, CardContent } from "../../components/ui/card";
import { Avatar, AvatarFallback } from "../../components/ui/avatar";
import { Badge } from "../../components/ui/badge";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { ChatInput } from "./ChatInput";
import type { ChatInputState } from "./types";

const SKILLS = [
  { command: "/charter", label: "Charter" },
  { command: "/scope-manager", label: "Scope Manager" },
  { command: "/time-manager", label: "Time Manager" },
  { command: "/status-tracker", label: "Status Tracker" },
  { command: "/kb-lookup", label: "KB Lookup" },
  { command: "/general", label: "General" },
] as const;

export function ChatPane() {
  const selectedSessionId = useAppStore((s) => s.selectedSessionId);
  const selectedSkill = useAppStore((s) => s.selectedSkill);
  const selectedModel = useAppStore((s) => s.selectedModel);
  const setSelectedSkill = useAppStore((s) => s.setSelectedSkill);
  const { data: worklog } = useWorklog(selectedSessionId);
  const {
    streamMessage,
    isStreaming,
    traceEvents,
    error,
    pendingUserMessage
  } = useAgentStream(selectedSessionId);
  const [input, setInput] = useState("");
  const [copiedMessageId, setCopiedMessageId] = useState<number | null>(null);
  const messagesEndRef = useRef<HTMLDivElement>(null);

  // Reset skill to "general" when session changes
  useEffect(() => {
    setSelectedSkill("/general");
  }, [selectedSessionId, setSelectedSkill]);

  // Memoize worklogMessages to avoid recreating array on every render
  const worklogMessages = useMemo(() => {
    return worklog?.entries || [];
  }, [worklog?.entries]);

  // Merge worklog messages with pending user message for display
  const messages = useMemo(() => {
    return worklogMessages;
  }, [worklogMessages]);

  // Note: pendingUserMessage is now managed per-session in the stream store
  // It will be automatically cleared when the stream completes or errors

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages, traceEvents, pendingUserMessage, isStreaming]);

  const handleSend = async (inputState: ChatInputState) => {
    // Prevent any default behavior
    if (!inputState.message.trim() || isStreaming) {
      return;
    }

    if (!selectedSessionId) {
      console.error("No session selected");
      return;
    }

    const text = inputState.message.trim();
    // Use selected skill (defaults to "/general")
    const skillToUse = selectedSkill && selectedSkill.trim() ? selectedSkill.trim() : "/general";

    // Append information about uploaded files to the message
    let messageToSend = text;
    const uploadedFiles = inputState.attachments.filter(a => a.status === 'uploaded');
    if (uploadedFiles.length > 0) {
      const fileInfos = uploadedFiles.map(f => `- ${f.name} (path: ${f.url})`).join('\n');
      messageToSend = `${text}\n\n[Uploaded Files:\n${fileInfos}]`;
      console.log('Including uploaded files in message:', uploadedFiles);
    }

    // TODO: When backend is ready, send the full inputState with:
    // - thinkingMode: inputState.thinkingMode
    // - attachments: inputState.attachments
    //
    // For now, log these for debugging
    if (inputState.thinkingMode !== 'balanced' ||
        inputState.attachments.length > 0) {
      console.log('ChatInput features (ready for backend integration):', {
        thinkingMode: inputState.thinkingMode,
        attachmentsCount: inputState.attachments.length,
      });
    }

    // Clear input immediately
    // pendingUserMessage will be set by the stream manager when stream starts
    setInput("");

    try {
      await streamMessage(messageToSend, skillToUse, selectedModel, {
        webSearchEnabled: inputState.webSearchEnabled,
      });
      // Keep selectedSkill sticky - don't reset it after sending
    } catch (err) {
      // Restore input on error (pendingUserMessage will be cleared by stream manager)
      setInput(text);
      console.error("Failed to send message:", err);
    }
  };

  // Note: handleKeyDown is no longer needed - ChatInput handles keyboard shortcuts internally

  const handleCopy = async (text: string, messageId: number) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedMessageId(messageId);
      setTimeout(() => setCopiedMessageId(null), 2000);
    } catch (err) {
      console.error("Failed to copy text:", err);
    }
  };

  if (!selectedSessionId) {
    return (
      <div className="flex h-full items-center justify-center bg-muted/30">
        <div className="text-center space-y-2">
          <div className="h-16 w-16 mx-auto rounded-full bg-muted flex items-center justify-center mb-4">
            <Bot className="h-8 w-8 text-muted-foreground" />
          </div>
          <p className="text-lg font-semibold text-foreground">No session selected</p>
          <p className="text-sm text-muted-foreground">Select a session from the sidebar to start chatting</p>
        </div>
      </div>
    );
  }

  return (
    <div
      className="flex h-full flex-col bg-background"
      onKeyDown={(e) => {
        // Prevent keyboard events from bubbling to parent tabs
        // Only stop propagation for Enter key to prevent tab switching
        if (e.key === "Enter" && (e.target as HTMLElement).tagName === "INPUT") {
          e.stopPropagation();
        }
      }}
    >
      {/* Messages - Scrollable area */}
      <div className="flex-1 overflow-y-auto">
        <div className="max-w-4xl mx-auto px-4 md:px-6 py-4 md:py-6 space-y-4 md:space-y-6">
          {/* Error display */}

          {/* Error display */}
          {error && !isStreaming && (
            <div className="rounded-lg bg-destructive/10 border border-destructive/20 p-3 md:p-4 animate-in fade-in">
              <div className="flex items-center gap-2">
                <div className="h-2 w-2 rounded-full bg-destructive shrink-0" />
                <div className="text-sm font-medium text-destructive">Error</div>
              </div>
              <div className="text-sm text-destructive/80 mt-1">{error}</div>
            </div>
          )}

          {messages.length === 0 && !pendingUserMessage ? (
            <div className="flex flex-col items-center justify-center py-16 text-center">
              <div className="h-20 w-20 rounded-full bg-primary/10 flex items-center justify-center mb-4">
                <Bot className="h-10 w-10 text-primary" />
              </div>
              <h3 className="text-lg font-semibold text-foreground mb-2">Start a conversation</h3>
              <p className="text-sm text-muted-foreground max-w-md">
                Ask questions, get insights, or explore your knowledge base. Select a skill above or start typing.
              </p>
            </div>
          ) : (
            <>
              {messages.map((entry, idx) => (
              <div key={idx} className="space-y-3 md:space-y-4">
                {/* User Message */}
                <div className="flex gap-2 md:gap-4 items-start">
                  <Avatar className="h-8 w-8 md:h-9 md:w-9 border-2 border-background shadow-[0_1px_2px_0_rgba(0,0,0,0.05)] shrink-0">
                    <AvatarFallback className="bg-primary text-primary-foreground">
                      <User className="h-4 w-4 md:h-5 md:w-5" />
                    </AvatarFallback>
                  </Avatar>
                  <div className="flex-1 space-y-1 md:space-y-2 min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="text-xs md:text-sm font-semibold text-foreground">You</span>
                    </div>
                    <div className="rounded-lg rounded-tl-none bg-muted/50 px-3 py-2 md:px-4 md:py-3 text-xs md:text-sm text-foreground leading-relaxed shadow-[0_1px_2px_0_rgba(0,0,0,0.05)] break-words">
                      {entry.user}
                    </div>
                  </div>
                </div>

                {/* Assistant Message */}
                <div className="flex gap-4 items-start">
                  <Avatar className="h-9 w-9 border-2 border-background shadow-[0_1px_2px_0_rgba(0,0,0,0.05)]">
                    <AvatarFallback className="bg-primary text-primary-foreground">
                      <Bot className="h-5 w-5" />
                    </AvatarFallback>
                  </Avatar>
                  <div className="flex-1 space-y-2">
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-semibold text-foreground">Assistant</span>
                      {entry.skill && (
                        <Badge variant="secondary" className="text-xs">
                          {entry.skill_display || entry.skill}
                        </Badge>
                      )}
                    </div>
                    <div className="group relative rounded-lg rounded-tl-none bg-card border border-border px-4 py-3 text-sm text-foreground leading-relaxed shadow-[0_1px_2px_0_rgba(0,0,0,0.05)]">
                      <div className="prose prose-sm dark:prose-invert max-w-none prose-headings:font-semibold prose-p:my-2 prose-pre:bg-muted prose-pre:border prose-pre:border-border prose-code:text-sm prose-code:bg-muted/50 prose-code:px-1 prose-code:py-0.5 prose-code:rounded prose-strong:font-semibold prose-ul:my-2 prose-ol:my-2 prose-li:my-1">
                        <ReactMarkdown remarkPlugins={[remarkGfm]}>
                          {entry.assistant}
                        </ReactMarkdown>
                      </div>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="absolute top-2 right-2 h-7 w-7 opacity-0 group-hover:opacity-100 transition-opacity"
                        onClick={() => handleCopy(entry.assistant, idx)}
                        title="Copy to clipboard"
                      >
                        {copiedMessageId === idx ? (
                          <Check className="h-3.5 w-3.5 text-green-600" />
                        ) : (
                          <Copy className="h-3.5 w-3.5" />
                        )}
                      </Button>
                    </div>
                  </div>
                </div>
              </div>
              ))}

              {/* Pending user message (optimistic UI) */}
              {pendingUserMessage && (
                <div className="space-y-4">
                  {/* User Message */}
                  <div className="flex gap-4 items-start">
                    <Avatar className="h-9 w-9 border-2 border-background shadow-[0_1px_2px_0_rgba(0,0,0,0.05)]">
                      <AvatarFallback className="bg-primary text-primary-foreground">
                        <User className="h-5 w-5" />
                      </AvatarFallback>
                    </Avatar>
                    <div className="flex-1 space-y-2">
                      <div className="flex items-center gap-2">
                        <span className="text-sm font-semibold text-foreground">You</span>
                      </div>
                      <div className="rounded-lg rounded-tl-none bg-muted/50 px-4 py-3 text-sm text-foreground leading-relaxed shadow-[0_1px_2px_0_rgba(0,0,0,0.05)]">
                        {pendingUserMessage}
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Assistant Thinking Indicator - show when streaming, regardless of pending message */}
              {isStreaming && (
                <div className="flex gap-2 md:gap-4 items-start animate-in fade-in slide-in-from-bottom-2 duration-300">
                  <Avatar className="h-8 w-8 md:h-9 md:w-9 border-2 border-background shadow-[0_1px_2px_0_rgba(0,0,0,0.05)] shrink-0">
                    <AvatarFallback className="bg-primary text-primary-foreground">
                      <Bot className="h-4 w-4 md:h-5 md:w-5" />
                    </AvatarFallback>
                  </Avatar>
                  <div className="flex-1 space-y-1 md:space-y-2 min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="text-xs md:text-sm font-semibold text-foreground">Assistant</span>
                    </div>
                    <div className="rounded-lg rounded-tl-none bg-card border border-border px-3 py-2 md:px-4 md:py-3 text-xs md:text-sm text-foreground leading-relaxed shadow-[0_1px_2px_0_rgba(0,0,0,0.05)]">
                      <div className="flex items-center gap-2 text-muted-foreground">
                        <Loader2 className="h-3 w-3 md:h-4 md:w-4 animate-spin" />
                        <span>Thinking...</span>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </>
          )}

          {/* Live trace events */}
          {isStreaming && traceEvents.length > 0 && (
            <Card className="mt-4 border-primary/20 bg-primary/5 shadow-[0_1px_3px_0_rgba(0,0,0,0.1)]">
              <CardContent className="p-4">
                <div className="flex items-center gap-2 mb-3">
                  <Loader2 className="h-4 w-4 animate-spin text-primary" />
                  <div className="text-sm font-semibold text-foreground">Live Steps</div>
                </div>
                <div className="space-y-2 max-h-48 overflow-y-auto">
                  {traceEvents.map((ev, idx) => (
                    <div key={idx} className="flex items-start gap-2 text-xs">
                      <span className="text-muted-foreground font-mono">[{ev.kind}]</span>
                      <span className="text-foreground">{ev.title}</span>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}

          <div ref={messagesEndRef} />
        </div>
      </div>

      {/* Input Area with Modern ChatInput Component */}
      <ChatInput
        value={input}
        onChange={setInput}
        onSend={handleSend}
        disabled={isStreaming}
        placeholder="Type your message..."
        skill={selectedSkill}
        skills={SKILLS}
        onSkillChange={setSelectedSkill}
      />
    </div>
  );
}

